export type WhiteLabelColorKey = 'primary' | 'accent' | 'background' | 'text' | 'success' | 'attention' | 'warning' | 'error';
export type WhiteLabelColors = Partial<Record<WhiteLabelColorKey, string>>;
export type WhiteLabelLogoKey = 'logo' | 'logoSmall' | 'logoWhite' | 'favicon';
export type WhiteLabelLogos = Partial<Record<WhiteLabelLogoKey, string>>;

type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;

export const isHexColor = (value?: string): value is string => !!value && HEX_PATTERN.test(value);

const toRgb = (hex: string): Rgb => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)) as Rgb;

const toHex = (rgb: Rgb) => `#${rgb.map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;

/** share of the second color (0..1) in the mix */
const mix = (from: Rgb, to: Rgb, share: number): Rgb => from.map((v, i) => v + (to[i] - v) * share) as Rgb;

// Shares of white / black in the shades of the primary color, picked so that the default blue (#006edd) gives the shades DataDoc has now
const PRIMARY_SHADES: [number, Rgb, number][] = [
  [50, WHITE, 0.93],
  [100, WHITE, 0.85],
  [200, WHITE, 0.63],
  [300, WHITE, 0.25],
  [450, BLACK, 0.06],
  [500, BLACK, 0.2],
  [600, BLACK, 0.35],
  [700, BLACK, 0.5],
  [800, BLACK, 0.68],
  [900, BLACK, 0.8],
];

// Share of white in the greys: from the text color (grey-0) to almost white (grey-6)
const GREY_SHARES = [0.27, 0.38, 0.55, 0.755, 0.81, 0.86, 0.915];

/** Backgrounds of the states (success, error, ...) are the state color mixed with white */
const STATE_BG_SHARE = 0.82;

const STATES: [WhiteLabelColorKey, string][] = [
  ['success', 'success'],
  ['attention', 'attention'],
  ['warning', 'warning'],
  ['error', 'error'],
];

/**
 * CSS variables of the theme: the base colors of the workspace give all the shades the interface uses.
 * Only what is set is returned, the rest of the variables stay as they are in main.css.
 */
export const getThemeVariables = (colors: WhiteLabelColors = {}): Record<string, string> => {
  const variables: Record<string, string> = {};
  const { primary, accent, background, text } = colors;

  if (isHexColor(primary)) {
    const base = toRgb(primary);
    variables['--primary-400'] = toHex(base);
    PRIMARY_SHADES.forEach(([shade, to, share]) => (variables[`--primary-${shade}`] = toHex(mix(base, to, share))));
    variables['--primary-color'] = 'var(--primary-400)';
    variables['--primary-color-dark'] = 'var(--primary-500)';
    variables['--primary-color-bg'] = 'var(--primary-100)';
    variables['--primary-color-light'] = 'var(--primary-300)';
  }

  // the accent is the light variant of the primary color (hover, links on a dark background)
  if (isHexColor(accent)) {
    variables['--primary-300'] = toHex(toRgb(accent));
    variables['--primary-color-light'] = 'var(--primary-300)';
  }

  if (isHexColor(text)) {
    const base = toRgb(text);
    variables['--color-black-primary'] = toHex(base);
    variables['--body-color'] = 'var(--color-black-primary)';
    GREY_SHARES.forEach((share, index) => (variables[`--grey-${index}`] = toHex(mix(base, WHITE, share))));
  }

  if (isHexColor(background)) {
    variables['--grey-7'] = toHex(toRgb(background));
    variables['--body-bg'] = 'var(--grey-7)';
  }

  STATES.forEach(([key, name]) => {
    const color = colors[key];
    if (!isHexColor(color)) return;
    variables[`--${name}-color`] = toHex(toRgb(color));
    variables[`--${name}-color-bg`] = toHex(mix(toRgb(color), WHITE, STATE_BG_SHARE));
  });

  return variables;
};

/** Text of a style rule with the variables (for the <style> element of the page) */
export const getThemeCss = (colors: WhiteLabelColors = {}, selector = ':root') => {
  const variables = Object.entries(getThemeVariables(colors));
  return variables.length ? `${selector} { ${variables.map(([name, value]) => `${name}: ${value};`).join(' ')} }` : '';
};
